import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Milliseconds, NodeEdgeMapper, roundDecimals, TimeBucketUnits } from '@iotv/datamodel';
import { SortOrder } from '@iotv/iotv-v3-types';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { NetWorkDAO } from '../../../../data/NetworkDAO';
import { SteppingLoadingModal } from '../../../../Popup/Loading';
import { ObjectViewType } from '../../../../types';
import { useColumnVisibilityForDataGrid } from '../../../../v2/components/generic/ObjectsDataTable/hooks/useColumnVisibility';
import { getGatewayDevices } from '../../../DeviceManagement/tabs/Wideawake/GatewayDevices';
import { StripedDataGrid } from '../../../factories/DataGrid';
import { GatewayAnalysisColumnMap, GatewayAnalysisListViewDefintion } from './GatewayAnalysisHeaders';
const DATA_DAYS = 7;
const maxCols = 20;
const metricsQuery = async (days = 7) => {
    const query = {
        units: TimeBucketUnits.DAY.valueOf(),
        duration: 7,
        fromDateValue: dayjs().subtract(days, 'day'),
        toDateValue: dayjs(),
        sortOrder: SortOrder.DESCENDING,
    };
    const { data } = await NetWorkDAO.geGatewayMetricsFromTimestream(query);
    if (data) {
        return data;
    }
};
const getFilteredGateway = (gatewayList, time) => {
    return gatewayList;
    // let isAWOL = gatewayList.filter(gateway => gateway.isAWOL || time - gateway.lastSeen > Milliseconds.DAY); // gateway.isAWOL);
    // return isAWOL;
};
const GatewayAnalysis = ({ gatewayList }) => {
    const [gatewayMetrics, setGatewayMetrics] = useState();
    const [gatewayDevices, setGatewayDevices] = useState();
    const [formattedData, setFormattedData] = useState();
    const [addState, setAddState] = useState(0);
    const [max, setMax] = useState(undefined);
    const externalColumnVisibility = Object.fromEntries(Object.keys(GatewayAnalysisListViewDefintion)
        .slice(maxCols)
        .map((k) => [k, false]));
    useEffect(() => {
        metricsQuery(DATA_DAYS).then((val) => {
            if (val) {
                setGatewayMetrics(val);
            }
        });
    }, []);
    useEffect(() => {
        updateDeviceGateways(gatewayList);
    }, [gatewayList]);
    async function staggeredPairing(gatewayList) {
        const pairing = [];
        setMax(gatewayList.length);
        for (const gateway of gatewayList) {
            const gatewayDevs = await getGatewayDevices({
                sourceNode: gateway,
                ExclusiveStartKey: undefined,
                Limit: undefined,
            });
            pairing.push({
                parent: gateway,
                children: gatewayDevs.filter((gatewayDev) => gatewayDev.type === 'Device'),
            });
            setAddState(pairing.length);
            // Add a delay between iterations (e.g., 500ms)
            await new Promise((resolve) => setTimeout(resolve, 250));
        }
        return pairing;
    }
    const makeDevicePairings = async (gatewayList, now) => {
        const pairing = await staggeredPairing(gatewayList);
        Promise.all(pairing).then((res) => {
            setMax(undefined);
            setAddState(0);
            const objList = res
                .map((pair) => {
                // construct edges
                const parent = pair.parent;
                let children = pair.children;
                children = children.map((device) => {
                    const isAWOL = now - device.lastSeen > Milliseconds.DAY;
                    return { ...device, isAWOL: isAWOL };
                });
                const edges = children.map((child) => {
                    return { pk: `${parent.pk}`, sk: `${child.sk}` };
                });
                return [...children, ...edges, parent];
            })
                .flat();
            const mapper = new NodeEdgeMapper();
            mapper.loadItems(objList);
            setGatewayDevices(mapper);
        });
    };
    const updateDeviceGateways = (gateways) => {
        const now = Date.now();
        makeDevicePairings(getFilteredGateway(gateways, now), now);
    };
    useEffect(() => {
        if (gatewayMetrics && gatewayDevices) {
            // get all gateways
            const gateways = gatewayDevices.getNodesOfType('Gateway');
            // update SF
            const updatedGateway = gateways.map((gateway) => {
                let localCopy = { ...gateway };
                // gateway devices
                const devices = gatewayDevices.getChildNodes(gateway);
                const factor = 100 / devices.length;
                const res = devices.reduce((acc, cur) => {
                    const sf = cur.sf;
                    if (sf && sf >= 7 && sf <= 12) {
                        // acc[sf as number]++; // accumulate
                        acc[sf] += factor; // percentage
                    }
                    return acc;
                }, { 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0 });
                Object.keys(res).forEach((key) => {
                    const numKey = Number(key); // Convert key from string to number
                    res[numKey] = Math.round(res[numKey]);
                });
                const foundGateway = gatewayMetrics.find((val) => val.id === gateway.pk);
                if (foundGateway) {
                    localCopy = { ...foundGateway, ...localCopy };
                    if (foundGateway.uplinks) {
                        const expectedCount = foundGateway.droppedMsgs + foundGateway.uplinks;
                        const acceptance = roundDecimals((foundGateway.uplinks / expectedCount) * 100, 3);
                        localCopy = { ...localCopy, expectedCount, acceptance };
                    }
                    if (devices) {
                        const expectedCount15 = devices.length * DATA_DAYS * 4 * 24;
                        const acceptance15 = expectedCount15 !== 0 && foundGateway.uplinks < expectedCount15 ? roundDecimals((foundGateway.uplinks / expectedCount15) * 100, 3) : '';
                        localCopy = { ...localCopy, expectedCount15, acceptance15 };
                    }
                }
                return { ...localCopy, sfArray: res, devices: devices.length };
            });
            setFormattedData(updatedGateway);
        }
    }, [gatewayMetrics, gatewayDevices]);
    const [columnVisibilityModel, setColumnVisibilityModel, onColumnVisibilityModelChange] = useColumnVisibilityForDataGrid({
        dataTableReference: 'networkDataTable',
        objectTypeId: 'networkData',
        viewType: ObjectViewType.List,
        externalColumnVisibility,
    });
    const dataGridProps = {
        // onCellClick: handleCellClick,
        density: 'compact',
        columns: Object.entries(GatewayAnalysisColumnMap).map(([k, v]) => ({ field: k, ...v })),
        rows: formattedData || gatewayList || [],
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        // loading: formattedData === undefined, // CRA: 2026 01 — disabled as sometimes (always?) doesn't finish loading
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        rowHeight: 32,
        columnHeaderHeight: 40,
    };
    return (_jsxs(_Fragment, { children: [_jsx(StripedDataGrid, { style: { fontSize: 'inherit' }, ...dataGridProps }), max && _jsx(SteppingLoadingModal, { current: addState, max: max })] }));
};
export default GatewayAnalysis;
